import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supaFetch } from '../lib/supabase.js';
import { queryKeys } from '../lib/queryKeys.js';

// ─── Cache-shape helpers ──────────────────────────────────────────────────────
// fetchAllProducts returns { data: [...] }. TanStack stores this raw envelope
// in cache BEFORE select() unwraps it, so setQueryData callbacks receive the
// envelope object — not a plain array. These helpers keep optimistic updates
// safe regardless of cache shape, and also guard against null (empty table).
function rawToArray(old) {
  if (Array.isArray(old)) return old;
  if (old && Array.isArray(old.data)) return old.data;
  return [];
}
function patchRaw(old, nextArray) {
  if (Array.isArray(old)) return nextArray;
  return { ...(old || {}), data: nextArray };
}

// ─── fetchAllProducts ─────────────────────────────────────────────────────────
// Supabase PostgREST caps responses at max_rows (default 1000) regardless of
// the Range header value. We page through in chunks of 1000 and concatenate
// until we receive fewer rows than requested, signalling we've hit the end.
async function fetchAllProducts() {
  const PAGE_SIZE = 1000;
  let all = [];
  let from = 0;
  while (true) {
    const to = from + PAGE_SIZE - 1;
    const { data } = await supaFetch(
      'GET', 'products', null,
      '?order=created_at.desc',
      { 'Range': `${from}-${to}`, 'Prefer': 'return=representation' }
    );
    const rows = data ?? [];
    all = all.concat(rows);
    if (rows.length < PAGE_SIZE) break; // last page reached
    from += PAGE_SIZE;
  }
  return { data: all };
}

// ─── useProducts ──────────────────────────────────────────────────────────────
// Admin view — fetches ALL products including soft-deleted ones so the admin
// can see and restore inactive entries.
export function useProducts() {
  return useQuery({
    queryKey:  queryKeys.admin.products(),
    queryFn:   fetchAllProducts,
    select:    (result) => result.data ?? [],
    staleTime: 5 * 60 * 1000,
    gcTime:    10 * 60 * 1000,
  });
}

// ─── useSaveProduct ───────────────────────────────────────────────────────────
export function useSaveProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ product, isNew }) => {
      if (isNew) {
        await supaFetch('POST', 'products',
          { ...product, created_at: new Date().toISOString() });
      } else {
        await supaFetch('PATCH', 'products', product, `?id=eq.${product.id}`);
      }
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.productsRoot() });
    },
  });
}

// ─── useDeleteProduct ─────────────────────────────────────────────────────────
// Hard-deletes a single product (used only by batch-undo to remove newly
// inserted rows). The standard admin UI uses useToggleActive instead.
export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => supaFetch('DELETE', 'products', null, `?id=eq.${id}`),

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.productsRoot() });
    },
  });
}

// ─── useToggleActive ─────────────────────────────────────────────────────────
// Soft-deletes (is_active=false) or restores (is_active=true) a single product.
export function useToggleActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, active }) =>
      supaFetch('PATCH', 'products', { is_active: active }, `?id=eq.${id}`),

    onMutate: async ({ id, active }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.admin.productsRoot() });
      const snapshot = queryClient.getQueryData(queryKeys.admin.products());
      queryClient.setQueryData(queryKeys.admin.products(), (old) =>
        patchRaw(old, rawToArray(old).map(p => p.id === id ? { ...p, is_active: active } : p))
      );
      return { snapshot };
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot !== undefined)
        queryClient.setQueryData(queryKeys.admin.products(), ctx.snapshot);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.productsRoot() });
    },
  });
}

// ─── useUpdateProductField ────────────────────────────────────────────────────
export function useUpdateProductField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, patch }) =>
      supaFetch('PATCH', 'products', patch, `?id=eq.${id}`),

    onMutate: async ({ id, patch }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.admin.productsRoot() });
      const snapshot = queryClient.getQueryData(queryKeys.admin.products());
      queryClient.setQueryData(queryKeys.admin.products(), (old) =>
        patchRaw(old, rawToArray(old).map(p => p.id === id ? { ...p, ...patch } : p))
      );
      return { snapshot };
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot !== undefined)
        queryClient.setQueryData(queryKeys.admin.products(), ctx.snapshot);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.productsRoot() });
    },
  });
}

// ─── useBulkUpdateProducts ────────────────────────────────────────────────────
export function useBulkUpdateProducts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ids, patch }) => {
      for (const id of ids) {
        await supaFetch('PATCH', 'products', patch, `?id=eq.${id}`);
      }
    },

    onMutate: async ({ ids, patch }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.admin.productsRoot() });
      const snapshot = queryClient.getQueryData(queryKeys.admin.products());
      queryClient.setQueryData(queryKeys.admin.products(), (old) =>
        patchRaw(old, rawToArray(old).map(p => ids.has(p.id) ? { ...p, ...patch } : p))
      );
      return { snapshot };
    },

    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot !== undefined)
        queryClient.setQueryData(queryKeys.admin.products(), ctx.snapshot);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.productsRoot() });
    },
  });
}

// ─── useBulkDeleteProducts ────────────────────────────────────────────────────
// Hard-deletes — kept for legacy/emergency use. Admin UI now uses
// useBulkUpdateProducts with { is_active: false } for soft-deletes.
export function useBulkDeleteProducts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ids) => {
      for (const id of ids) {
        await supaFetch('DELETE', 'products', null, `?id=eq.${id}`);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.productsRoot() });
    },
  });
}

// ─── useUpsertImport ─────────────────────────────────────────────────────────
// Calls the import_products_v2 RPC which does INSERT … ON CONFLICT (sku) DO UPDATE.
// The RPC stamps every touched row with batch_id so the undo feature can find them.
export function useUpsertImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ rows, batchId }) =>
      supaFetch('POST', 'rpc/import_products_v2',
        { rows, batch_id: batchId }, ''
      ),

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.productsRoot() });
    },
  });
}

// ─── useAutoTagProducts ───────────────────────────────────────────────────────
export function useAutoTagProducts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patches) => {
      for (const { id, category } of patches) {
        await supaFetch('PATCH', 'products', { category }, `?id=eq.${id}`);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.productsRoot() });
    },
  });
}

// ─── useProductVariants ───────────────────────────────────────────────────────
export function useProductVariants(productId) {
  return useQuery({
    queryKey:  ['productVariants', productId],
    queryFn:   () =>
      supaFetch(
        'GET', 'product_variants', null,
        `?product_id=eq.${encodeURIComponent(productId)}&order=sort_order.asc,created_at.asc`
      ),
    select:    (result) => result.data ?? [],
    enabled:   !!productId,
    staleTime: 5 * 60 * 1000,
    gcTime:    10 * 60 * 1000,
  });
}

// ─── useSaveVariant ───────────────────────────────────────────────────────────
export function useSaveVariant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ product_id, id, ...rest }) =>
      supaFetch('POST', 'product_variants',
        { id, product_id, ...rest },
        '',
        { 'Prefer': 'resolution=merge-duplicates,return=minimal' }
      ),

    onSuccess: (_data, variant) => {
      queryClient.invalidateQueries({ queryKey: ['productVariants', variant.product_id] });
    },
  });
}

// ─── useDeleteVariant ─────────────────────────────────────────────────────────
export function useDeleteVariant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }) =>
      supaFetch('DELETE', 'product_variants', null, `?id=eq.${id}`),

    onSuccess: (_data, { product_id }) => {
      queryClient.invalidateQueries({ queryKey: ['productVariants', product_id] });
    },
  });
}
